import { Typography } from '@mui/material';
import ArticleList from 'components/articles/ArticleList';
import { FilterBar } from 'components/FilterBar';
import { ArticleTypeMap, LangMap } from 'config';
import MiniSearch from 'minisearch';
import React from 'react';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import stringSimilarity from 'string-similarity-js';
import { ThunkDispatch } from 'redux-thunk';
import { getUserPlaylists } from 'store/data/user/selectors';
import { getArticlesFiltered, getArticlesFilteredSecond } from 'store/data/articles/selectors';
import { getDataSorted } from 'store/data/generic/selectors';
import { updateAppBarTitle } from 'store/temp/actions';
import { ApplicationState, Article, DownloadStatus, UserPlaylist } from 'types';

interface TracksListProps {
	playlist?: UserPlaylist;
	articles: Article[];
	downloadingMedia: Article[];
	queuedMedia: Article[];
	origArticles?: Article[];
	audioRecords?: Article[];
	// fetchArticle: (href: string) => Promise<Article>;
	updateAppBarTitle: (title: string) => void;
}

const filtersMeta = {
	articleType: {
		label: 'Content Type',
		type: 'checkbox',
		renderType: 'togglebar',
		value: (record) => record.articleType,
		optionLabel: (option) => ArticleTypeMap[option] || option,
	},
	lang: {
		label: 'Language',
		type: 'custom',
		renderType: 'togglebar',
		value: (record) => {
			if (record.lang && record.lang !== 'undefined') {
				return record.lang;
			}
			return record.title?.en ? 'en' : 'hi';
		},
		dependsOn: ['articleType', 'publishTime'],
		optionLabel: (option) => LangMap[option],
	},
	playbackState: {
		label: 'Playback Status',
		type: 'custom',
		renderType: 'togglebar',
		value: (record) => {
			return record.__playbackState ?? 'Not Started';
		},
		dependsOn: ['articleType', 'publishTime', 'lang'],
		optionLabel: (option) => option,
	},
	publishTime: {
		label: 'Date',
		type: 'daterange',
	},
	group: {
		label: 'Category/Genre',
		type: 'checkbox',
		dependsOn: ['articleType', 'lang', 'publishTime'],
		optionLabel: (option) => option,
	},
};

class TracksList extends React.Component<TracksListProps> {
	constructor(props: any) {
		super(props);

		props.updateAppBarTitle(`Playlist [${props.playlist?.title ?? ''}]`);
	}

	render() {
		let { articles, playlist, downloadingMedia, queuedMedia, origArticles } = this.props;
		if (!playlist) {
			return null;
		}

		let isDownloadingMedia = playlist.id === '0' && (downloadingMedia.length > 0 || queuedMedia.length > 0);

		let downloading: any[] = [];
		let queued: any[] = [];
		if (isDownloadingMedia) {
			if (articles.length > 0) {
				(articles as any[]).unshift({
					articleType: 'component',
					__component: (
						<Typography variant="body2" color="textSecondary" style={{ marginLeft: 8, marginTop: 16 }}>
							Downloaded [{articles.length}]:
						</Typography>
					),
				});
			}
			if (queuedMedia.length > 0) {
				queued = [
					{
						articleType: 'component',
						__component: (
							<Typography variant="body2" color="textSecondary" style={{ marginLeft: 8, marginTop: 16 }}>
								Queued for Download [{queuedMedia.length}]:
							</Typography>
						),
					},
					...queuedMedia,
				];
				(articles as any[]).unshift(...queued);
			}
			if (downloadingMedia.length > 0) {
				downloading = [
					{
						articleType: 'component',
						__component: (
							<Typography variant="body2" color="textSecondary" style={{ marginLeft: 8, marginTop: 16 }}>
								Currently Downloading [{downloadingMedia.length}]:
							</Typography>
						),
					},
					...downloadingMedia,
				];
				(articles as any[]).unshift(...downloading);
			}
		}

		return (
			<>
				{!isDownloadingMedia && (
					<FilterBar
						sortBy={[
							'Original Order',
							'Newest First',
							'Oldest First',
							'Title - Ascending',
							'Title - Descending',
							'Duration - Low to High',
							'Duration - High to Low',
						]}
						sortValue="Original Order"
						recordType={`playlist-${playlist.id}`}
						records={origArticles}
						filtersMeta={filtersMeta}
					/>
				)}
				<ArticleList recordType="" avoidVirtual avoidFilterBar records={articles} />
			</>
		);
	}
}

function mapStateToProps(state: ApplicationState, { playlistId, playlist: fetchedPlaylist }: { playlistId: string; playlist?: UserPlaylist }) {
	let playlists = getUserPlaylists(state);
	let playlist = fetchedPlaylist ?? playlists[playlistId];
	if (!playlist) {
		return {};
	}

	let tracks = Object.values(playlist.tracks).sort((a, b) => a.order - b.order);

	let articles: Article[] = [];
	let articlesById = state.dataState.articles.byId;

	let downloadProgress = state.offlineState.audio;
	let downloadingMedia = Object.keys(downloadProgress)
		.filter((id) => downloadProgress[id]?.progress?.status === DownloadStatus.InProgress)
		.map((id) => articlesById[id])
		.sort((a, b) => a.mediaLength - b.mediaLength);
	let queuedMedia = Object.keys(downloadProgress)
		.filter((id) => downloadProgress[id]?.progress?.status === DownloadStatus.Queued)
		.map((id) => articlesById[id])
		.sort((a, b) => a.mediaLength - b.mediaLength);

	// Build the initial articles array
	for (let i = 0; i < tracks.length; i++) {
		let article = articlesById[tracks[i].articleId];
		if (article) {
			if (playlist.id === '0') {
				if (downloadProgress[article.id]?.progress?.status === DownloadStatus.Complete || !downloadProgress[article.id]) {
					articles.push(article);
				}
			} else {
				articles.push(article);
			}
		}
	}

	// Store original articles for filter bar
	let origArticles = [...articles];

	// Apply filtering and sorting for non-downloading playlists
	if (playlist.id !== '0' || (downloadingMedia.length === 0 && queuedMedia.length === 0)) {
		let recordType = `playlist-${playlist.id}`;
		let filters = state.uxState.filters[recordType];
		let sortValue = (state.uxState.sort && state.uxState.sort[recordType]) ?? 'Original Order';

		// Apply sorting
		articles = getDataSorted(articles, sortValue, recordType + '-' + sortValue) as Article[];

		// Apply filtering
		if (filters) {
			articles = getArticlesFiltered(state, articles, filters, filtersMeta);
		}

		// Apply search filtering
		if (state.tempState.searchTerm.length > 0) {
			let miniSearch = new MiniSearch({
				fields: ['enTitle', 'hiTitle'],
				storeFields: ['title'],
				searchOptions: {
					fuzzy: 0.17,
					combineWith: 'AND',
					prefix: true,
				},
				extractField: (doc, field) => {
					if (field === 'id') {
						return doc.id;
					}
					if (typeof doc.title === 'string' || field === 'title') {
						return doc.title;
					}

					return field === 'enTitle' ? doc.title.en : doc.title.hi;
				},
			});
			miniSearch.addAll(articles);

			let filtered = miniSearch.search(state.tempState.searchTerm);

			filtered.forEach((v) => {
				let ratingEn = stringSimilarity(state.tempState.searchTerm, v.title?.en ?? '', Math.ceil(state.tempState.searchTerm.length * 0.7));
				let ratingHi = stringSimilarity(state.tempState.searchTerm, v.title?.hi ?? '', Math.ceil(state.tempState.searchTerm.length * 0.7));
				v.rating = Math.max(ratingEn, ratingHi);
			});

			articles = filtered
				.sort((a, b) => (b.rating !== a.rating ? b.rating - a.rating : b.score - a.score))
				.map((v) => {
					let doc = state.dataState.articles.byId[v.id];
					return doc;
				});
		}
	}

	// Get audio records for play all functionality
	const audioFilter = { mediaType: { audio: true, text: false, video: false } };
	let audioRecords = getArticlesFilteredSecond(state, articles, audioFilter, filtersMeta);

	return {
		playlist,
		articles,
		downloadingMedia,
		queuedMedia,
		origArticles,
		audioRecords,
	};
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, unknown, AnyAction>) {
	return {
		// fetchArticle: async (href: string) => {
		// 	return await dispatch(
		// 		fetchArticleData({ href: href }, { cacheStrategy: CacheStrategy.CacheFallingBackToNetwork })
		// 	);
		// },
		updateAppBarTitle: (title: string) => {
			dispatch(updateAppBarTitle(title));
		},
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(TracksList);
